import React, { useState } from 'react'
import { motion } from 'motion/react'
import { MapPinIcon, ArrowRightIcon } from '@heroicons/react/20/solid'

interface LodgeHour {
  day: string
  open: string | null
  close: string | null
  closed: boolean
}

interface Lodge {
  id: number
  name: string
  lodge_number: string
  full_address: string
  distance: string | null
  phone_number: string | null
  email: string | null
  hours: LodgeHour[]
  url: string
  image_url?: string | null
}

interface LodgeFinderProps {
  csrfToken: string
  searchUrl: string
}

/**
 * Lodges upload their own photos, so plenty of results have none. The fallback
 * is the lodge number set as a plate in the display face - it identifies the
 * result and stays inside the page's palette, which the previous
 * violet-to-indigo gradient did not.
 */
function LodgePlate({ number }: { number: string }) {
  return (
    <div
      aria-hidden="true"
      className="flex h-full w-full items-center justify-center bg-[color:var(--theme-card)]"
    >
      <span className="font-display text-5xl font-bold tracking-tight text-[color:var(--theme-fg)] opacity-[0.13]">
        #{number}
      </span>
    </div>
  )
}

function ResultSkeleton() {
  return (
    <div className="border-b border-[color:var(--theme-border-soft)] p-5 sm:even:border-l">
      <div className="aspect-[16/10] w-full animate-pulse rounded-lg bg-[color:var(--theme-hover-strong)]" />
      <div className="mt-4 h-5 w-2/3 animate-pulse rounded bg-[color:var(--theme-hover-strong)]" />
      <div className="mt-2.5 h-4 w-5/6 animate-pulse rounded bg-[color:var(--theme-hover)]" />
    </div>
  )
}

export function LodgeFinder({ csrfToken, searchUrl }: LodgeFinderProps) {
  const [zipcode, setZipcode] = useState('')
  const [lodges, setLodges] = useState<Lodge[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)

  // Debounced realtime search
  React.useEffect(() => {
    const trimmedZipcode = zipcode.trim()

    // Don't search if zipcode is empty or less than 5 digits
    if (trimmedZipcode.length < 5) {
      setLodges([])
      setError(null)
      setSearched(false)
      return
    }

    // Debounce the search
    const timeoutId = setTimeout(async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(searchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({ zipcode: trimmedZipcode }),
        })

        const data = await response.json()
        if (!response.ok || data.error) {
          throw new Error(data.error || 'Failed to search')
        }

        setLodges(data.lodges)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
        setLodges([])
      } finally {
        setLoading(false)
        setSearched(true)
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timeoutId)
  }, [zipcode, searchUrl, csrfToken])

  const showResults = lodges.length > 0 && !loading
  const showEmpty = searched && !loading && !error && lodges.length === 0

  return (
    <div className="mx-auto max-w-7xl px-4 pt-14 pb-24 sm:px-6 lg:px-8">
      {/* Left-aligned and split rather than a centred column: the search
                controls stay put on desktop while results scroll beside them. */}
      <div className="grid gap-x-16 gap-y-10 lg:grid-cols-[22rem_1fr] lg:items-start">
        <div className="lg:sticky lg:top-10">
          <h1 className="display-2 text-[color:var(--theme-fg)]">Find a lodge near you</h1>
          <p className="measure mt-4 text-base leading-7 text-[color:var(--theme-muted)]">
            Every Elks lodge on LodgeLink, searchable by zipcode. Results cover a 50 mile radius.
          </p>

          <div className="mt-8 flex flex-col gap-2">
            <label
              htmlFor="lodge-zipcode"
              className="text-sm font-medium text-[color:var(--theme-fg)]"
            >
              Zipcode
            </label>
            <input
              id="lodge-zipcode"
              name="zipcode"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              value={zipcode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 5)
                setZipcode(value)
              }}
              aria-describedby="lodge-zipcode-help"
              className="numeric w-full rounded-lg border border-[color:var(--theme-border)] bg-[color:var(--theme-surface)] px-4 py-3 text-lg tracking-[0.08em] text-[color:var(--theme-fg)] transition-colors duration-fast ease-out-soft placeholder:text-[color:var(--theme-muted)] placeholder:tracking-normal hover:border-[color:var(--theme-border-strong)]"
              pattern="[0-9]{5}"
              maxLength={5}
            />
            <p
              id="lodge-zipcode-help"
              className="text-sm text-[color:var(--theme-muted)]"
              aria-live="polite"
            >
              {error ? (
                <span className="text-[color:var(--theme-brand-text)]">{error}</span>
              ) : loading ? (
                'Searching...'
              ) : showResults ? (
                `${lodges.length} ${lodges.length === 1 ? 'lodge' : 'lodges'} near ${zipcode}`
              ) : (
                'Five digits, and results appear as you type.'
              )}
            </p>
          </div>
        </div>

        <div>
          {loading && (
            <div className="grid border-t border-[color:var(--theme-border-soft)] sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <ResultSkeleton key={i} />
              ))}
            </div>
          )}

          {showResults && (
            <div className="grid border-t border-[color:var(--theme-border-soft)] sm:grid-cols-2">
              {lodges.map((lodge, index) => (
                <div
                  key={lodge.id}
                  className="border-b border-[color:var(--theme-border-soft)] sm:even:border-l"
                >
                  <motion.a
                    href={lodge.url}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.38,
                      delay: Math.min(index * 0.05, 0.3),
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="group block p-5 transition-colors duration-fast ease-out-soft hover:bg-[color:var(--theme-hover)]"
                  >
                    <div className="aspect-[16/10] w-full overflow-hidden rounded-lg">
                      {lodge.image_url ? (
                        <img
                          src={lodge.image_url}
                          alt={`${lodge.name} lodge building`}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-normal ease-[var(--ease-out-soft)] group-hover:scale-[1.02]"
                        />
                      ) : (
                        <LodgePlate number={lodge.lodge_number} />
                      )}
                    </div>

                    <div className="mt-4 flex items-baseline justify-between gap-3">
                      <h2 className="display-3 text-[color:var(--theme-fg)] transition-colors duration-fast ease-out-soft group-hover:text-[color:var(--theme-brand-text)]">
                        {lodge.name}
                      </h2>
                      {lodge.distance && (
                        // LodgeFinderSerializer already renders this as
                        // "0.7 miles"; the old markup appended " mi" on top,
                        // producing "0.7 miles mi".
                        <span className="numeric shrink-0 font-mono text-sm text-[color:var(--theme-muted)]">
                          {lodge.distance}
                        </span>
                      )}
                    </div>

                    <p className="numeric mt-1 text-sm text-[color:var(--theme-muted)]">
                      Lodge #{lodge.lodge_number}
                    </p>

                    <p className="mt-3 flex items-start gap-2 text-sm leading-6 text-[color:var(--theme-muted)]">
                      <MapPinIcon className="mt-1 size-4 shrink-0 text-[color:var(--theme-brand-text)]" />
                      {lodge.full_address}
                    </p>

                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--theme-fg)]">
                      View lodge
                      <ArrowRightIcon className="size-4 transition-transform duration-fast ease-[var(--ease-out-soft)] group-hover:translate-x-0.5" />
                    </span>
                  </motion.a>

                  <a
                    href={`${lodge.url}#request-info`}
                    className="block px-5 pb-5 text-sm font-medium text-[color:var(--theme-brand-text)] hover:underline"
                  >
                    Request information
                  </a>
                </div>
              ))}
            </div>
          )}

          {showEmpty && (
            <div className="border border-dashed border-[color:var(--theme-border)] px-6 py-16 text-center">
              <MapPinIcon className="mx-auto size-7 text-[color:var(--theme-muted)]" />
              <p className="mt-4 text-base font-semibold text-[color:var(--theme-fg)]">
                No lodges within 50 miles of {zipcode}
              </p>
              <p className="measure mx-auto mt-2 text-sm leading-6 text-[color:var(--theme-muted)]">
                Try a nearby zipcode. Lodges appear here once they set up their LodgeLink profile,
                so a lodge you know of may not be listed yet.
              </p>
            </div>
          )}

          {!loading && !searched && !error && (
            <div className="border border-dashed border-[color:var(--theme-border)] px-6 py-16 text-center">
              <p className="text-base font-semibold text-[color:var(--theme-fg)]">
                Results appear here
              </p>
              <p className="measure mx-auto mt-2 text-sm leading-6 text-[color:var(--theme-muted)]">
                Enter a zipcode to see lodge names, distances, and addresses.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
